import { useEffect, useRef, MutableRefObject } from "react";
import LogUtil from "@/betby/utils/LogUtil";
import { sendUrlChangeMessage } from "@/betby/models/host/unityMessageService";

interface UseBetbyUrlChangeProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  initInfo: any;
  isInitializedRef: MutableRefObject<boolean>;
  pathname?: string | null;
  basename?: string;
}

function normalizeBasename(basename?: string): string | null {
  if (!basename) {
    return null;
  }

  const trimmed = basename.trim();
  if (!trimmed) {
    return null;
  }

  const prefixed = trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
  return prefixed.replace(/\/+$/, "").toLowerCase();
}

function matchesBasename(currentUrl: string, basename?: string): boolean {
  const normalizedBasename = normalizeBasename(basename);
  if (!normalizedBasename) {
    return false;
  }

  try {
    const currentPathname = new URL(
      currentUrl,
      window.location.origin,
    ).pathname.toLowerCase();
    return (
      currentPathname === normalizedBasename ||
      currentPathname.startsWith(`${normalizedBasename}/`)
    );
  } catch {
    return currentUrl.toLowerCase().includes(normalizedBasename);
  }
}

export const useBetbyUrlChange = ({
  initInfo,
  isInitializedRef,
  pathname,
  basename,
}: UseBetbyUrlChangeProps) => {
  const lastUrlRef = useRef(
    typeof window !== "undefined" ? window.location.href : "",
  );

  // basename 导航：结合 Next.js pathname 监听所有路由的变化，即使组件被重新触发渲染也能保存旧 URL
  useEffect(() => {
    if (!initInfo || !isInitializedRef.current) return;

    const checkUrlChange = () => {
      const currentUrl = window.location.href;
      if (currentUrl !== lastUrlRef.current) {
        lastUrlRef.current = currentUrl;

        if (matchesBasename(currentUrl, basename)) {
          LogUtil.debug("[BetbyRenderer basename] Relative URL changed:", {
            basename,
            to: currentUrl,
          });
          sendUrlChangeMessage(currentUrl);
        }
      }
    };

    // 当 useEffect 被重新抛出（比如 pathname 变化），立刻比对一次
    checkUrlChange();

    // 监听 popstate（浏览器前进/后退）
    window.addEventListener("popstate", checkUrlChange);

    // 拦截 pushState 和 replaceState（用于捕获无刷新的单页路由跳转）
    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;

    window.history.pushState = function (...args) {
      originalPushState.apply(this, args);
      setTimeout(checkUrlChange, 0); // next-tick 以保证 window.location.href 已是最新的
    };
    window.history.replaceState = function (...args) {
      originalReplaceState.apply(this, args);
      setTimeout(checkUrlChange, 0);
    };

    // 兜底的轮询检测
    const intervalId = setInterval(checkUrlChange, 500);

    return () => {
      window.history.pushState = originalPushState;
      window.history.replaceState = originalReplaceState;
      window.removeEventListener("popstate", checkUrlChange);
      clearInterval(intervalId);
    };
  }, [basename, initInfo, isInitializedRef, pathname]);
};
